// A DE's set-up in edit mode: the "Add digital employee" steps again, each saved on its own —
// never a re-deploy (DE-WIZARD, review #12). UX pass 1.2 (2026-10-27): no screen of its own any
// more; each step is a section of the DE page's Setup and KPIs tabs (`DeSetupPart`), and the old
// `/agents/de/{id}/edit?step=` redirects to that section (`STEP_SECTION`).
//
//   template, seat   what it was set up with (a new template is a new deploy; a seat moves in
//                    Setup › Seats), read only;
//   job              standing orders, Saved vs Deployed (`GET …/standing-orders`), saved as the
//                    override of its template (or its own before one, `template_id: null`);
//   KPIs             its seat's KPIs (`GET …/job`, read from the seat), targets and which it
//                    reports; removals are explicit; applied at once;
//   models & budget  the role-to-model map (the same override) and the seat's budget;
//   channels         home channel, joins (at once), approver of its posts (the seat's settings);
//   review           readiness, what is saved but not deployed, and the one action left: Deploy
//                    or Submit for a Draft, "Deploy changes" for a self-serve DE whose saved
//                    standing orders or models differ from what runs (05: the owner or an
//                    admin; done-for-you changes are the operator's redeploy, no approval needed).
import { ApiError, newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Button,
  buttonVariants,
  cn,
  InfoTip,
  Notice,
  SettingsSection,
  Textarea,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArmchairIcon,
  ClipboardCheckIcon,
  LayoutTemplateIcon,
  RocketIcon,
  ScrollTextIcon,
  SendIcon,
} from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../../api/client";
import type { DeProfile } from "../../../api/types";
import { keepFocus } from "../../../focus/focus";
import { useCurrency } from "../../agents/agent-parts";
import { budgetsQuery, invalidateAgents } from "../../agents/agent-queries";
import { orgChartQuery } from "../../chart/chart-queries";
import { chatKeys } from "../../chat/chat-queries";
import { type CompanyAccess, isManager } from "../../company";
import { formatDateTime } from "../../format";
import { problemLines } from "../../operator/templates/template-parts";
import { useOperatorName } from "../../operator-name";
import { keys, metricsQuery } from "../../queries";
import { Facts, LoadError, Loading } from "../../screen-parts";
import { useFeature } from "../../use-feature";
import type { DeTab } from "../de-profile";
import { DeLiveBadge, liveness } from "../liveness";
import { ReadinessStrip } from "../readiness";
import { channelNames, useJoinableChannels, useJoinedChannels } from "./channels";
import {
  currentMonth,
  type DeJobView,
  type Duty,
  jobKey,
  jobQuery,
  type KpiRow,
  mapOfRows,
  mayStage,
  rowsOfMap,
  STEP_KEYS,
  type StepKey,
  selfServeTemplatesQuery,
  targetValid,
} from "./model";
import {
  BudgetField,
  ChannelsFields,
  type ChartSeat,
  hasPerson,
  KpiEditor,
  ModelsEditor,
  OrdersField,
} from "./steps";

const ordersKey = (co: string, de: string) => ["company", co, "de", de, "standing-orders"] as const;
const overrideKey = (co: string, de: string) => ["company", co, "de", de, "override"] as const;

export const isStepKey = (v: unknown): v is StepKey => (STEP_KEYS as unknown[]).includes(v);

/**
 * UX pass 1.2: the old edit screen's steps, each now a section of the DE page (`?tab=`): the
 * template and seat facts sit in Identity & runtime, the job in Job & standing orders, the KPI
 * targets and reporting in KPIs › Reporting, models in Models & budget, channels in Channels &
 * approval, the review in Deploy.
 */
export const STEP_SECTION = {
  template: "settings",
  seat: "settings",
  job: "job",
  kpis: "reporting",
  models: "budget",
  channels: "channels",
  review: "deploy",
} as const satisfies Record<StepKey, DeTab>;

/** The DE page's sections that were the edit screen's steps (managers: the job read is theirs). */
export type SetupPart = "facts" | "job" | "reporting" | "models" | "channels" | "deploy";

/**
 * One former step on the DE page, with the job read (`GET …/job`, managers) it needs. The page
 * shows these to managers only; anyone else gets the line the edit screen gave them.
 */
export function DeSetupPart({
  api,
  company,
  de,
  part,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
  part: SetupPart;
}) {
  const operator = useOperatorName();
  const manager = isManager(company);
  const job = useQuery({ ...jobQuery(api, company.id, de.membership_id), enabled: manager });
  if (!manager)
    return (
      <Notice tone="neutral">
        A DE's set-up is edited by the company's owner, admins and {operator}.
      </Notice>
    );
  if (job.isPending) return <Loading label="Loading the set-up" rows={3} />;
  if (job.isError) return <LoadError what="The set-up" error={job.error} />;
  const props = { api, company, de, job: job.data };
  switch (part) {
    case "facts":
      return (
        <div className="flex flex-col gap-4">
          <SeatFacts {...props} />
          <TemplateFacts {...props} />
        </div>
      );
    case "job":
      return <JobEdit {...props} />;
    case "reporting":
      return <KpisEdit key={job.data.version} {...props} />;
    case "models":
      return <ModelsEdit {...props} />;
    case "channels":
      return <ChannelsEdit key={job.data.version} {...props} />;
    case "deploy":
      return <ReviewEdit {...props} />;
  }
}

type StepProps = { api: Api; company: CompanyAccess; de: DeProfile; job: DeJobView };

function useOrders(api: Api, co: string, deId: string) {
  return useQuery({
    queryKey: ordersKey(co, deId),
    queryFn: () => api.client.digitalEmployees.standingOrders({ params: { co, id: deId } }),
    retry: false,
  });
}

function useOverride(api: Api, co: string, deId: string) {
  return useQuery({
    queryKey: overrideKey(co, deId),
    queryFn: () => api.client.digitalEmployees.templateOverride({ params: { co, id: deId } }),
    retry: false,
  });
}

const pathLabel = (path: "self_serve" | "done_for_you", operator: string) =>
  path === "self_serve"
    ? "Deployed by the company (self-serve)"
    : `Set up by ${operator} (done-for-you)`;

function TemplateFacts({ api, company, de, job }: StepProps) {
  const operator = useOperatorName();
  const orders = useOrders(api, company.id, de.membership_id);
  return (
    <SettingsSection
      icon={LayoutTemplateIcon}
      title="Template"
      actions={
        <InfoTip label="About the template">
          What it was set up from. Another template is a new deploy, not an edit.
        </InfoTip>
      }
    >
      <Facts
        items={[
          {
            label: "Template key",
            value: <code className="font-mono text-xs">{de.template_key}</code>,
          },
          {
            label: "Deployed template",
            value: orders.data?.template_name ?? (orders.isPending ? "…" : "None yet"),
          },
          { label: "Path", value: pathLabel(job.path, operator) },
          {
            label: "Verification",
            value: de.verification_state === "verified" ? "Verified" : "Unverified",
          },
        ]}
      />
    </SettingsSection>
  );
}

function SeatFacts({ api, company, de }: StepProps) {
  const chart = useQuery(orgChartQuery(api, company.id));
  const teams = (chart.data?.team_members ?? [])
    .filter((m) => m.seat_id === de.seat?.id)
    .map((m) => chart.data?.teams.find((t) => t.id === m.team_id)?.name)
    .filter(Boolean);
  return (
    <SettingsSection
      icon={ArmchairIcon}
      title="Name & seat"
      actions={
        <span className="inline-flex items-center gap-1.5">
          <InfoTip label="About moving a seat">
            A seat moves in two steps in Setup › Seats (unassign, then assign).
          </InfoTip>
          <Link
            to="/c/$slug/setup/seats"
            params={{ slug: company.slug }}
            search={keepFocus}
            className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}
          >
            Open Seats
          </Link>
        </span>
      }
    >
      <Facts
        items={[
          { label: "Name", value: workerName(de.display_name) },
          { label: "Seat", value: de.seat?.title ?? "No seat" },
          {
            label: "Reports to",
            value: de.supervisor
              ? `${de.supervisor.title}${de.supervisor.name ? ` · ${de.supervisor.name}` : ""}`
              : "—",
          },
          { label: "Teams", value: teams.length ? teams.join(", ") : "None" },
        ]}
      />
    </SettingsSection>
  );
}

/** "Deploy changes": a new self-serve deploy of the version it runs (05: owner or admin). */
function useDeployChanges(api: Api, company: CompanyAccess, de: DeProfile) {
  const qc = useQueryClient();
  const [idem, setIdem] = useState(newIdempotencyKey);
  return useMutation({
    mutationFn: (versionId: string) =>
      api.client.agents.deploySelfServe({
        params: { co: company.id },
        idempotencyKey: idem,
        body: { de_membership_id: de.membership_id, template_version_id: versionId },
      }),
    onError: (e) => {
      if (e instanceof ApiError) setIdem(newIdempotencyKey());
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["company", company.id, "de", de.membership_id] });
      void qc.invalidateQueries({ queryKey: keys.digitalEmployees(company.id) });
      void invalidateAgents(qc, company.id);
    },
  });
}

function DeployChanges({
  api,
  company,
  de,
  job,
  versionId,
  pending,
}: StepProps & { versionId: string | null; pending: boolean }) {
  const operator = useOperatorName();
  const deploy = useDeployChanges(api, company, de);
  // WF-FIX: a self-hosted DE is not deployed through AiGentPanel: no Deploy for it.
  if (!pending || de.self_hosted) return null;
  const deploying = de.readiness.state === "deploying" || deploy.isSuccess;
  return (
    <div className="flex flex-col gap-2" data-slot="deploy-changes">
      {job.path === "self_serve" && versionId ? (
        mayStage(company) ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              disabled={deploy.isPending || deploying}
              onClick={() => deploy.mutate(versionId)}
            >
              <RocketIcon aria-hidden="true" />
              {deploying ? "Deploying…" : deploy.isPending ? "Deploying…" : "Deploy changes"}
            </Button>
            <span className="text-xs text-muted-foreground">
              A new deploy of the same template with what is saved; no approval needed.
            </span>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            The company's owner or an admin deploys the change.
          </p>
        )
      ) : job.path === "done_for_you" && de.deploy_ref ? (
        <p className="text-xs text-muted-foreground" data-slot="htva-redeploys">
          {operator} deploys the change with its next redeploy (no approval needed).
        </p>
      ) : null}
      {deploy.isError ? (
        <Notice tone="danger" role="alert" title="The change was not deployed">
          {problemLines(deploy.error, "Try again.").join(" · ")}
        </Notice>
      ) : null}
    </div>
  );
}

function JobEdit({ api, company, de, job }: StepProps) {
  const orders = useOrders(api, company.id, de.membership_id);
  if (orders.isPending) return <Loading label="Loading the standing orders" rows={4} />;
  if (orders.isError)
    return orders.error instanceof ApiError && orders.error.status === 403 ? (
      <Notice tone="neutral">Standing orders are shown to admins and the DE's supervisor.</Notice>
    ) : (
      <LoadError what="The standing orders" error={orders.error} />
    );
  return (
    <JobForm
      // One form per DE: on the DE page other sections (Models) write the same override, and a
      // new version must never wipe what is being typed here (Codex UX-B r1 #1).
      key={de.membership_id}
      api={api}
      company={company}
      de={de}
      job={job}
      data={orders.data}
    />
  );
}

/** Why the company's orders cannot go back to the template's (the API keeps no empty override). */
class OrdersOnlyOverride extends Error {}

type Orders = Awaited<ReturnType<Api["client"]["digitalEmployees"]["standingOrders"]>>;

function JobForm({
  api,
  company,
  de,
  job,
  data,
}: StepProps & {
  data: Orders;
}) {
  const qc = useQueryClient();
  const deployedId = useId();
  const next = data.next ?? "";
  const [text, setText] = useState(next);
  // The saved text the draft started from: a newer saved text replaces an untouched draft only.
  const [base, setBase] = useState(next);
  if (next !== base) {
    setBase(next);
    if (text === base) setText(next);
  }
  const [saved, setSaved] = useState(false);
  const dirty = text !== next;
  const save = useMutation({
    // `null` clears the company's standing orders: the DE runs its template's again. An
    // override is never empty, so that keeps its model map (read when asked, not on load);
    // an override of the orders alone has nothing left to keep.
    mutationFn: async (orders: string | null) => {
      const params = { co: company.id, id: de.membership_id };
      const role_model_map =
        orders === null
          ? (
              await qc.fetchQuery({
                queryKey: overrideKey(company.id, de.membership_id),
                queryFn: () => api.client.digitalEmployees.templateOverride({ params }),
              })
            ).override?.role_model_map
          : undefined;
      if (orders === null && !role_model_map) throw new OrdersOnlyOverride();
      return api.client.digitalEmployees.putTemplateOverride({
        params,
        body: {
          template_id: data.template_id,
          standing_orders_text: orders,
          ...(role_model_map ? { role_model_map } : {}),
        },
      });
    },
    onSuccess: async (_, orders) => {
      // Back to the template's: the box shows the template's text, not the old draft.
      if (orders === null) setText(data.template_text ?? "");
      await qc.invalidateQueries({ queryKey: ordersKey(company.id, de.membership_id) });
      await qc.invalidateQueries({ queryKey: overrideKey(company.id, de.membership_id) });
      setSaved(true);
    },
  });
  return (
    <form
      className="flex flex-col gap-4"
      data-slot="edit-job"
      data-pending={data.pending ? "true" : "false"}
      onSubmit={(e) => {
        e.preventDefault();
        setSaved(false);
        save.mutate(text);
      }}
    >
      <SettingsSection
        icon={ScrollTextIcon}
        title="Job & standing orders"
        actions={
          <span className="inline-flex items-center gap-1.5">
            {data.pending ? (
              <Badge variant="warning" dot data-slot="orders-state">
                Saved · not deployed
              </Badge>
            ) : data.deployed ? (
              <Badge variant="success" dot data-slot="orders-state">
                Deployed
              </Badge>
            ) : null}
            <InfoTip label="About standing orders">
              {data.template_id
                ? `The override of ${data.template_name ?? "its template"}: a running DE gets a saved change with its next deploy.`
                : "Its own standing orders, before any template: every deploy builds with them."}
            </InfoTip>
          </span>
        }
      >
        <OrdersField
          value={text}
          onChange={setText}
          templateText={data.template_text}
          required={false}
          readOnly={!data.can_edit}
          id="edit-orders"
          hint="Given word for word to every worker it starts."
        />
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium" id={deployedId}>
            Deployed
          </span>
          {data.deployed?.text != null ? (
            <Textarea
              aria-labelledby={deployedId}
              rows={6}
              readOnly
              value={data.deployed.text}
              data-slot="orders-deployed"
              className="bg-sunken text-muted-foreground"
            />
          ) : (
            <p className="text-sm text-muted-foreground" data-slot="orders-deployed">
              {data.deployed
                ? `Deployed${data.deployed.deployed_at ? ` ${formatDateTime(data.deployed.deployed_at)}` : ""}; the bundle's text is not shown here.`
                : `${workerName(de.display_name)} runs nothing deployed yet.`}
            </p>
          )}
          {data.saved ? (
            <p className="text-xs text-muted-foreground">
              Saved v{data.saved.version} · {formatDateTime(data.saved.updated_at)}
            </p>
          ) : null}
        </div>
        {save.isError ? (
          <Notice tone="danger" role="alert" title="Not saved">
            {save.error instanceof OrdersOnlyOverride
              ? "The company's override changes only the standing orders, so it cannot be emptied. Paste the template's text and save to match it."
              : problemLines(save.error, "The standing orders could not be saved.").join(" · ")}
          </Notice>
        ) : saved ? (
          <Notice tone="success" role="status" data-slot="orders-saved-note">
            Saved.{" "}
            {data.deployed
              ? `${workerName(de.display_name)} gets them with its next deploy.`
              : "They go with its first deploy."}
          </Notice>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <DeployChanges
            api={api}
            company={company}
            de={de}
            job={job}
            versionId={data.template_version_id}
            pending={data.pending && Boolean(data.deployed)}
          />
          <span className="ml-auto flex gap-2">
            {/* The profile's old editor cleared the company's orders (back to the template's). */}
            {/* A saved text equal to the template's is still fixed: template changes skip it. */}
            {data.can_edit && data.template_id && data.saved?.text != null ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={save.isPending}
                data-slot="orders-reset"
                onClick={() => {
                  setSaved(false);
                  save.mutate(null);
                }}
              >
                Back to the template's
              </Button>
            ) : null}
            {data.can_edit ? (
              <Button type="submit" size="sm" disabled={!dirty || !text.trim() || save.isPending}>
                {save.isPending ? "Saving…" : "Save standing orders"}
              </Button>
            ) : null}
          </span>
        </div>
      </SettingsSection>
    </form>
  );
}

const rowOf = (k: DeJobView["kpis"][number]): KpiRow => ({
  metric_def_id: k.metric_def_id,
  name: k.name,
  unit: k.unit,
  target: k.weekly_target ?? "",
  reports: k.reported_by_agent,
  duty: (k.duty ?? "weekly") as Duty,
});

function KpisEdit({ api, company, de, job }: StepProps) {
  const qc = useQueryClient();
  const kpiOn = useFeature("kpi", company.id).enabled;
  const metrics = useInfiniteQuery({ ...metricsQuery(api, company.id), enabled: kpiOn });
  const [rows, setRows] = useState<KpiRow[]>(() => job.kpis.map(rowOf));
  const [removed, setRemoved] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const before = JSON.stringify(job.kpis.map(rowOf));
  const dirty = removed.length > 0 || JSON.stringify(rows) !== before;
  const save = useMutation({
    mutationFn: () =>
      api.client.digitalEmployees.putJob({
        params: { co: company.id, id: de.membership_id },
        body: {
          kpis: rows.map((k) => ({
            metric_def_id: k.metric_def_id,
            weekly_target: k.target.trim() ? k.target.trim() : null,
            reported_by_agent: k.reports,
            ...(k.reports ? { duty: k.duty } : {}),
          })),
          ...(removed.length ? { remove_metric_def_ids: removed } : {}),
        },
      }),
    onSuccess: (view) => {
      qc.setQueryData(jobKey(company.id, de.membership_id), view);
      void qc.invalidateQueries({ queryKey: ["company", company.id, "seat"] });
      setSaved(true);
    },
  });
  if (!kpiOn)
    return (
      <SettingsSection icon={ClipboardCheckIcon} title="KPIs">
        <Notice tone="neutral">KPIs need the KPI add-on.</Notice>
      </SettingsSection>
    );
  if (!job.seat_id)
    return (
      <SettingsSection icon={ClipboardCheckIcon} title="KPIs">
        <Notice tone="neutral">
          {workerName(de.display_name)} has no seat, so it has no KPIs.
        </Notice>
      </SettingsSection>
    );
  return (
    <form
      className="flex flex-col gap-4"
      data-slot="edit-kpis"
      onSubmit={(e) => {
        e.preventDefault();
        setSaved(false);
        save.mutate();
      }}
    >
      <SettingsSection
        icon={ClipboardCheckIcon}
        title="KPIs"
        description={`${de.seat?.title ?? "Its seat"}'s KPIs. Saved changes apply at once: no deploy.`}
      >
        <KpiEditor
          rows={rows}
          onChange={(next) => {
            setSaved(false);
            setRows(next);
          }}
          metrics={metrics.data?.pages.flatMap((p) => p.data) ?? []}
          removed={removed}
          onRemove={(id) => {
            if (job.kpis.some((k) => k.metric_def_id === id)) setRemoved((r) => [...r, id]);
          }}
        />
        {save.isError ? (
          <Notice tone="danger" role="alert" title="Not saved">
            {problemLines(save.error, "The KPIs could not be saved.").join(" · ")}
          </Notice>
        ) : saved ? (
          <Notice tone="success" role="status" data-slot="kpis-saved-note">
            Saved. The seat's scorecard uses them from now.
          </Notice>
        ) : null}
        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            disabled={!dirty || save.isPending || !rows.every((r) => targetValid(r.target))}
          >
            {save.isPending ? "Saving…" : "Save KPIs"}
          </Button>
        </div>
      </SettingsSection>
    </form>
  );
}

function ModelsEdit({ api, company, de, job }: StepProps) {
  const qc = useQueryClient();
  const co = company.id;
  const override = useOverride(api, co, de.membership_id);
  const orders = useOrders(api, co, de.membership_id);
  const budgets = useInfiniteQuery(budgetsQuery(api, co));
  const currency = useCurrency(api, co) ?? "USD";
  const [rows, setRows] = useState<ReturnType<typeof rowsOfMap> | null>(null);
  const [amount, setAmount] = useState("200");
  const [note, setNote] = useState<string | null>(null);
  const seatBudget = (budgets.data?.pages ?? [])
    .flatMap((p) => p?.data ?? [])
    .find((b) => b.subject_kind === "seat" && b.subject_id === de.seat?.id);
  const current = rowsOfMap(override.data?.override?.role_model_map);
  const shown = rows ?? current;
  const saveModels = useMutation({
    mutationFn: () =>
      api.client.digitalEmployees.putTemplateOverride({
        params: { co, id: de.membership_id },
        body: {
          template_id: orders.data?.template_id ?? null,
          role_model_map: mapOfRows(shown),
        },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: overrideKey(co, de.membership_id) });
      await qc.invalidateQueries({ queryKey: ordersKey(co, de.membership_id) });
      setRows(null);
      setNote("Models saved. They apply with the next deploy.");
    },
  });
  const addBudget = useMutation({
    mutationFn: () =>
      api.client.budgets.create({
        params: { co },
        idempotencyKey: newIdempotencyKey(),
        body: {
          subject_kind: "seat",
          subject_id: de.seat?.id as string,
          period_type: "month",
          period_key: currentMonth(),
          amount_minor: Math.round(Number.parseFloat(amount) * 100),
          currency: currency as "USD",
        },
      }),
    onSuccess: () => {
      void invalidateAgents(qc, co);
      setNote("Budget set.");
    },
  });
  if (override.isPending || orders.isPending || budgets.isPending)
    return <Loading label="Loading models and budget" rows={3} />;
  const savedAt = override.data?.override?.updated_at ?? null;
  const deployedAt = orders.data?.deployed?.deployed_at ?? null;
  const pending = Boolean(
    savedAt && deployedAt && savedAt > deployedAt && override.data?.override?.role_model_map,
  );
  const err = saveModels.error ?? addBudget.error;
  return (
    <SettingsSection
      icon={RocketIcon}
      title="Models & budget"
      actions={
        pending ? (
          <Badge variant="warning" dot data-slot="models-state">
            Saved · not deployed
          </Badge>
        ) : null
      }
    >
      <ModelsEditor rows={shown} onChange={setRows} required={job.path === "self_serve"} />
      {!override.data?.override?.role_model_map ? (
        <p className="text-xs text-muted-foreground">
          No models of its own: it runs its template's.
        </p>
      ) : null}
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          disabled={
            rows === null || Object.keys(mapOfRows(shown)).length === 0 || saveModels.isPending
          }
          onClick={() => saveModels.mutate()}
        >
          {saveModels.isPending ? "Saving…" : "Save models"}
        </Button>
      </div>
      {de.seat ? (
        seatBudget ? (
          <BudgetField
            amount=""
            onChange={() => undefined}
            currency={currency}
            month={currentMonth()}
            existing={seatBudget.period_type}
          />
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <BudgetField
              amount={amount}
              onChange={setAmount}
              currency={currency}
              month={currentMonth()}
              existing={null}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={addBudget.isPending || !(Number.parseFloat(amount) > 0)}
              onClick={() => addBudget.mutate()}
            >
              Set budget
            </Button>
          </div>
        )
      ) : null}
      {err ? (
        <Notice tone="danger" role="alert" title="Not saved">
          {problemLines(err, "Try again.").join(" · ")}
        </Notice>
      ) : note ? (
        <Notice tone="success" role="status" data-slot="models-saved-note">
          {note}
        </Notice>
      ) : null}
      <DeployChanges
        api={api}
        company={company}
        de={de}
        job={job}
        versionId={orders.data?.template_version_id ?? null}
        pending={pending}
      />
    </SettingsSection>
  );
}

function ChannelsEdit({ api, company, de, job }: StepProps) {
  const qc = useQueryClient();
  const co = company.id;
  const chatOn = useFeature("chat", co).enabled;
  const { joinable, list } = useJoinableChannels(api, co, chatOn);
  const joined = useJoinedChannels(api, co, de.membership_id, list);
  const chart = useQuery(orgChartQuery(api, co));
  const [home, setHome] = useState(job.home_channel_id ?? "");
  const [joins, setJoins] = useState<string[]>([]);
  const [approval, setApproval] = useState(job.post_approval.required);
  const [approver, setApprover] = useState(job.post_approval.approver_seat_id ?? "");
  const [done, setDone] = useState<string | null>(null);
  const seats: ChartSeat[] = chart.data?.seats ?? [];
  const save = useMutation({
    mutationFn: async () => {
      const view = await api.client.digitalEmployees.putJob({
        params: { co, id: de.membership_id },
        body: {
          home_channel_id: home || null,
          ...(job.seat_id
            ? {
                post_approval: {
                  required: approval,
                  approver_seat_id: approval && approver ? approver : null,
                },
              }
            : {}),
        },
      });
      const refused: string[] = [];
      const added: string[] = [];
      const member = new Set((joined ?? []).map((c) => c.id));
      for (const id of [...new Set([home, ...joins].filter(Boolean))].filter((x) => !member.has(x)))
        await api.client.channels
          .addMember({ params: { co, id }, body: { membership_id: de.membership_id } })
          .then(() => added.push(id))
          .catch((e: unknown) => {
            if (!(e instanceof ApiError && e.status === 409))
              refused.push(joinable.find((c) => c.id === id)?.label ?? "a channel");
          });
      return { view, refused, added };
    },
    onSuccess: ({ view, refused, added }) => {
      qc.setQueryData(jobKey(co, de.membership_id), view);
      for (const id of added) void qc.invalidateQueries({ queryKey: chatKeys.members(co, id) });
      setJoins([]);
      setDone(
        refused.length
          ? `Saved; it could not join ${refused.join(", ")}.`
          : "Saved. Channels and approval apply at once.",
      );
    },
  });
  return (
    <form
      className="flex flex-col gap-4"
      data-slot="edit-channels"
      onSubmit={(e) => {
        e.preventDefault();
        setDone(null);
        save.mutate();
      }}
    >
      <SettingsSection
        icon={SendIcon}
        title="Channels & access"
        description="Applies at once: no deploy."
      >
        <ChannelsFields
          channels={list}
          joined={joined}
          home={home}
          onHome={setHome}
          joins={joins}
          onJoins={setJoins}
          postApproval={approval}
          onPostApproval={setApproval}
          approver={approver}
          onApprover={setApprover}
          approverSeats={seats.filter((s) => s.status !== "closed" && hasPerson(s))}
          supervisorLabel={de.supervisor?.title ?? "its supervisor seat"}
        />
        {save.isError ? (
          <Notice tone="danger" role="alert" title="Not saved">
            {problemLines(save.error, "Try again.").join(" · ")}
          </Notice>
        ) : done ? (
          <Notice tone="success" role="status" data-slot="channels-saved-note">
            {done}
          </Notice>
        ) : null}
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save channels & access"}
          </Button>
        </div>
      </SettingsSection>
    </form>
  );
}

function ReviewEdit({ api, company, de, job }: StepProps) {
  const operator = useOperatorName();
  const qc = useQueryClient();
  const co = company.id;
  const orders = useOrders(api, co, de.membership_id);
  const agentsOn = useFeature("agents", co).enabled;
  const catalog = useQuery({
    ...selfServeTemplatesQuery(api, co),
    enabled: agentsOn && job.path === "self_serve" && mayStage(company),
  });
  const chatOn = useFeature("chat", co).enabled;
  const channels = useJoinableChannels(api, co, chatOn);
  const joined = useJoinedChannels(api, co, de.membership_id, channels.list);
  const home = channels.joinable.find((c) => c.id === job.home_channel_id);
  const deploy = useDeployChanges(api, company, de);
  const submit = useMutation({
    mutationFn: () =>
      api.client.digitalEmployees.putJob({
        params: { co, id: de.membership_id },
        body: { submit: true },
      }),
    onSuccess: (view) => {
      qc.setQueryData(jobKey(co, de.membership_id), view);
      void qc.invalidateQueries({ queryKey: keys.de(co, de.membership_id) });
      void qc.invalidateQueries({ queryKey: keys.digitalEmployees(co) });
    },
  });
  const draft = de.readiness.state === "draft";
  // A self-serve draft deploys the catalog's version of its template (the empty agent, a basic).
  const version =
    orders.data?.template_version_id ??
    catalog.data?.data.find((t) => t.template.key === de.template_key)?.version.id ??
    null;
  const err = deploy.error ?? submit.error;
  return (
    <SettingsSection icon={ClipboardCheckIcon} title="Review" actions={<DeLiveBadge de={de} />}>
      <ReadinessStrip readiness={de.readiness} path={job.path} liveLabel={liveness(de)?.label} />
      <Facts
        items={[
          { label: "Path", value: pathLabel(job.path, operator) },
          {
            label: "Standing orders",
            value: orders.data
              ? orders.data.pending
                ? orders.data.deployed
                  ? "Saved · not deployed"
                  : "Saved · goes with the first deploy"
                : orders.data.deployed
                  ? "Deployed"
                  : "None saved"
              : "…",
          },
          {
            label: "KPIs",
            value: job.kpis.length
              ? `${job.kpis.length} on the seat · ${job.reporting.length} reported by it`
              : "None",
          },
          ...(channels.list
            ? [
                { label: "Home channel", value: home ? home.label : "None" },
                {
                  label: "Channels",
                  value: joined ? channelNames(joined) : "…",
                },
              ]
            : []),
          {
            label: "Its posts",
            value: job.post_approval.required ? "Wait for approval" : "Posted directly",
          },
          {
            label: "Submitted",
            value: job.submitted_at ? formatDateTime(job.submitted_at) : "Not yet",
          },
        ]}
      />
      {de.self_hosted ? (
        // WF-FIX: saved here, used by its Hermes container; nothing to deploy from CompanyOS.
        <p className="text-xs text-muted-foreground" data-slot="self-hosted-no-deploy">
          Self-hosted on Hermes: no deploy.
        </p>
      ) : draft ? (
        job.path === "self_serve" ? (
          mayStage(company) && version ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                disabled={deploy.isPending}
                onClick={() => deploy.mutate(version, { onSuccess: () => submit.mutate() })}
              >
                <RocketIcon aria-hidden="true" />
                {deploy.isPending ? "Deploying…" : "Deploy"}
              </Button>
              <span className="text-xs text-muted-foreground">No approval needed.</span>
            </div>
          ) : (
            <Notice tone="neutral">
              The company's owner or an admin deploys it (Agents add-on).
            </Notice>
          )
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" disabled={submit.isPending} onClick={() => submit.mutate()}>
              <SendIcon aria-hidden="true" />
              {submit.isPending ? "Submitting…" : `Submit to ${operator}`}
            </Button>
            <span className="text-xs text-muted-foreground">
              {operator} stages the first deploy; the owner approves it.
            </span>
          </div>
        )
      ) : (
        <DeployChanges
          api={api}
          company={company}
          de={de}
          job={job}
          versionId={orders.data?.template_version_id ?? null}
          pending={Boolean(orders.data?.pending && orders.data.deployed)}
        />
      )}
      {err ? (
        <Notice tone="danger" role="alert" title="Not done">
          {problemLines(err, "Try again.").join(" · ")}
        </Notice>
      ) : null}
    </SettingsSection>
  );
}
